/**
 * Copy for the download section on the landing page (`#downloads`): where a
 * visitor gets each player.
 *
 * Kept out of `marketing.ts` because this copy has one job the rest of the
 * marketing site does not: it must stay truthful about what is downloadable
 * *today*. Positive-only (marketing/CLAIMS.md §0, F-19, 2026-09-27): a target
 * with no URL simply renders no button, so nothing here says what is missing,
 * pending or on the way. A store button label only appears once
 * `download-targets.ts` has a live listing URL for it. When a
 * target goes live the copy here does not change; only the URL in
 * `artifacts/web/src/lib/download-targets.ts` does.
 *
 * Claim rules that bind this file (marketing/CLAIMS.md F-9, F-10): name only
 * the stores each app is really on (the mobile app is on the App Store and
 * Google Play, the Android TV app on Google Play), and never mention Apple TV.
 */
export const downloads = {
  /** Nav and footer entry, pointing at the section on the landing page. */
  nav: {
    label: "Downloads",
  },

  section: {
    eyebrow: "Get the apps",
    heading: "Pick the player that fits the room.",
    body: "One account, four ways to play. Open a browser tab now, or install the app that suits the room you are covering.",
    note: "The mobile app is on the App Store and Google Play, and the Android TV app is on Google Play. The desktop apps for Mac and Windows and the Android TV app also download straight from here.",
    seeThem: "See every app running",
  },

  // Platform detection: a badge on the card that matches the visitor's device,
  // never a filter. Every platform stays visible whatever the browser reports.
  recommended: {
    badge: "Best match for this device",
  },

  web: {
    title: "Web player",
    body: "Open a tab on the machine wired to the speakers and sign the screen in once. It stays paired to that venue and follows the schedule from then on.",
    points: {
      install: "Nothing to install",
      anyBrowser: "Any modern browser, any operating system",
    },
    cta: "Open the web player",
  },

  desktop: {
    title: "Desktop",
    body: "The same player, installed on a Mac or a Windows PC. It caches the schedule and the media ahead of time, so a dropped connection does not stop the music.",
    points: {
      offline: "Keeps playing through an outage from its own cache",
      kiosk: "Kiosk lock with a PIN to exit",
    },
  },

  mobile: {
    title: "Phone and tablet",
    body: "A companion for whoever is standing in the venue. Sign in, see what every player is doing, change it, or hold to talk.",
    points: {
      control: "See and change what every player is doing",
      announce: "Hold to talk, live to any location",
    },
  },

  tv: {
    title: "Android TV",
    body: "For signage, a small box behind the display. It shows a pairing code, you claim it from the control room, and it follows the same schedule as the audio.",
    points: {
      pair: "Pair by reading the code on screen",
      signage: "Video, images, web pages and widgets",
    },
  },

  // Operator-only extras on the in-app /install page. A visitor sees the
  // platform and the button; an operator also needs to know which version a
  // button serves and to be able to paste the link into an email.
  app: {
    heading: "Downloads",
    openPage: "See the apps",
    version: "Version {version}",
    copyLink: "Copy link",
    linkCopied: "Link copied",
  },

  actions: {
    // macOS ships one build per chip; an arm64 dmg will not open on an Intel
    // Mac, so the two are named rather than guessed at for the visitor.
    macosAppleSilicon: "Mac · Apple Silicon",
    macosIntel: "Mac · Intel",
    windows: "Download for Windows",
    appStore: "App Store",
    playStore: "Google Play",
    // Android TV direct download (sideload onto a TV box).
    apk: "Download APK",
  },
} as const;
